import { Link, useParams, Navigate } from "react-router-dom";
import { Check, FileText, Clock } from "lucide-react";
import Seo from "@/components/site/Seo";
import PageHero from "@/components/site/PageHero";
import { Reveal } from "@/components/site/motion";
import { SectionHeading, CTABanner, FAQList } from "@/components/site/Blocks";
import { getService } from "@/data/services";
import { useT } from "@/i18n";

export default function ServicePage() {
  const { slug } = useParams();
  const { t } = useT();
  const s = getService(slug);
  if (!s) return <Navigate to="/" replace />;
  return (
    <>
      <Seo title={`${s.name} Advisory`} description={`${s.short} Discreet, compliance-first ${s.name.toLowerCase()} support from Immistop, subject to eligibility.`} />
      <PageHero eyebrow="Advisory Services" lines={[s.name]} subtitle={s.short} image={s.image} testid="service-hero">
        <Link to="/contact" className="btn-gold" data-testid="service-book-btn"><span>{t("cta.book")}</span></Link>
      </PageHero>

      <section className="bg-ivory section-lux" data-testid="service-overview">
        <div className="container-lux grid gap-20 lg:grid-cols-[1.2fr_0.8fr] lg:gap-28">
          <div>
            <SectionHeading eyebrow="Overview" lines={["A considered", "approach."]} />
            <Reveal><p className="lede mt-14 text-charcoal">{s.intro}</p></Reveal>
          </div>
          <Reveal delay={0.15} className="self-end">
            <p className="eyebrow-dark">What's included</p>
            <ul className="mt-8 border-t border-navy/10">
              {s.offerings.map((o) => <li key={o} className="flex gap-5 border-b border-navy/10 py-5 text-[17px] font-light text-charcoal"><Check className="mt-1.5 h-4 w-4 shrink-0 text-gold-deep" />{o}</li>)}
            </ul>
          </Reveal>
        </div>
      </section>

      <section className="bg-navy py-24 text-ivory md:py-32" data-testid="service-who-for">
        <div className="container-lux">
          <SectionHeading light eyebrow="Who It's For" lines={["Designed for", <em key="e" className="italic text-gold">discerning clients.</em>]} />
          <div className="mt-24 grid gap-x-14 gap-y-16 sm:grid-cols-2 lg:grid-cols-4">
            {s.whoFor.map((w, i) => (
              <Reveal key={w} delay={i * 0.08} className="border-t border-gold/25 pt-10">
                <span className="font-serif text-6xl font-light italic text-gold">0{i + 1}</span>
                <p className="mt-8 text-[17px] font-light leading-relaxed text-ivory/85">{w}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-ivory section-lux" data-testid="service-process">
        <div className="container-lux">
          <SectionHeading eyebrow="Process" lines={["Step by step."]} />
          <ol className="mt-24 grid gap-16 md:grid-cols-2 lg:grid-cols-4 lg:gap-14">
            {s.steps.map((st, i) => (
              <Reveal as="li" key={st.t} delay={i * 0.1} className="border-t border-gold pt-10">
                <span className="font-sans text-[11px] font-semibold tracking-[0.25em] text-gold-deep">STEP 0{i + 1}</span>
                <h3 className="mt-6 font-serif text-4xl font-light text-navy">{st.t}</h3>
                <p className="mt-5 text-[16px] font-light leading-relaxed text-charcoal-soft">{st.d}</p>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      <section className="bg-ivory-100 section-lux">
        <div className="container-lux grid gap-28 lg:grid-cols-2 lg:gap-24">
          <div data-testid="service-documents">
            <SectionHeading eyebrow="Documents Required" lines={["Typically requested."]} />
            <ul className="mt-16 border-t border-navy/10">
              {s.documents.map((d) => <Reveal as="li" key={d} className="flex gap-5 border-b border-navy/10 py-6 text-[17px] font-light leading-relaxed text-charcoal"><FileText className="mt-1 h-4 w-4 shrink-0 text-gold-deep" strokeWidth={1.25} />{d}</Reveal>)}
            </ul>
          </div>
          <div data-testid="service-timelines">
            <SectionHeading eyebrow="Indicative Timelines" lines={["Planning ahead."]} />
            <dl className="mt-16 border-t border-navy/10">
              {s.timelines.map((tl) => (
                <Reveal key={tl.label} className="flex flex-col justify-between gap-3 border-b border-navy/10 py-8 sm:flex-row sm:items-center">
                  <dt className="flex items-center gap-4 font-serif text-3xl font-light text-navy"><Clock className="h-4 w-4 text-gold-deep" strokeWidth={1.25} />{tl.label}</dt>
                  <dd className="text-[16px] font-light text-charcoal-soft">{tl.value}</dd>
                </Reveal>
              ))}
            </dl>
            <p className="mt-6 text-[13px] leading-relaxed text-charcoal-soft">All timelines are indicative only. Processing times are set by the relevant authority and may change without notice.</p>
          </div>
        </div>
      </section>

      <section className="bg-ivory section-lux" data-testid="service-faq">
        <div className="container-lux grid gap-16 lg:grid-cols-[0.8fr_1.2fr] lg:gap-24">
          <SectionHeading eyebrow="FAQ" lines={["Common", "questions."]} />
          <FAQList items={s.faqs} prefix="service-faq" />
        </div>
      </section>
      <CTABanner title={`Discuss ${s.name.toLowerCase()} in confidence.`} />
    </>
  );
}
